import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import mapper from '../../../../_endpoints'
import { usePost } from '../../../../_hooks/usePost'
import { useFetch } from '../../../../_hooks/useFetch'
import { useDelete } from '../../../../_hooks/useDelete'

export interface Project {
  id: string
  name: string
  icon: string
  color: string
  instructions?: string
  /** List/get responses return the string name (e.g. "Default"); request bodies use the numeric enum. */
  memoryMode?: number | string
  scopeType?: string
  scopeId?: string
  createdAt?: string
  lastModifiedAt?: string
}

export interface ProjectPayload {
  name: string
  icon: string
  color: string
  instructions: string
  memoryMode: number
}

export interface ProjectThread {
  id: string
  name: string
  agentId?: string
  projectId?: string
  preview?: string
  createdAt?: string
  lastModifiedAt?: string
}

export interface ProjectFile {
  id: string
  originalFileName: string
  contentType?: string
  createdAt?: string
  // Provenance: where the file came from. 'databox' files are references to
  // documents in a personal databox; 'upload' (default) are device uploads.
  source?: 'upload' | 'databox'
  databoxName?: string
  // For databox-sourced files, the owning personal databox id (needed to detach).
  databoxId?: string
}

// One personal databox attached to a project. `All` includes the whole databox;
// `SelectedSubset` pins specific documents/data products via the id arrays.
export interface ProjectDataBoxSelection {
  personalDataBoxId: string
  selectionMode: 'All' | 'SelectedSubset'
  resourceIds?: string[]
  dataProductIds?: string[]
}

export const useProjectService = () => {
  const [isLoadingProjects, fetchProjects] = useFetch('nexus')
  const [, fetchProjectById] = useFetch('nexus')
  const [isLoadingProjectThreads, fetchProjectThreads] = useFetch('nexus')
  const [isLoadingProjectFiles, fetchProjectFiles] = useFetch('nexus')
  const [isCreatingProject, createProjectPost] = usePost('nexus')
  const [isUpdatingProject, updateProjectPost] = usePost('nexus')
  const [isMovingThread, moveThreadPost] = usePost('nexus')
  const [isAddingFile, addFilePost] = usePost('nexus')
  const [isUploadingFile, uploadResourcePost] = usePost('nexus')
  const [isLoadingDataBoxes, fetchProjectDataBoxes] = useFetch('nexus')
  const [isSavingDataBoxes, saveProjectDataBoxes] = usePost('nexus')
  const [isRemovingThread, removeThreadItem] = useDelete('nexus')
  const [isRemovingFile, removeFileItem] = useDelete('nexus')
  const [isDeletingProject, deleteProjectItem] = useDelete('nexus')

  const [projects, setProjects] = useState<Project[]>([])

  const normaliseList = (response: any): Project[] =>
    Array.isArray(response)
      ? response
      : response?.projects ?? response?.items ?? response?.data ?? []

  // Create/update/get responses wrap the entity in a singular `project` key.
  const normaliseProject = (response: any): Project | null => {
    const project = response?.project ?? response
    return project?.id ? (project as Project) : null
  }

  const refetchProjects = async (offset = 0, limit = 20): Promise<Project[]> => {
    try {
      const url = `${mapper.projects.getAll}?offset=${offset}&limit=${limit}`
      const response = await fetchProjects(url)
      const list = normaliseList(response)
      setProjects(list)
      return list
    } catch {
      setProjects([])
      return []
    }
  }

  useEffect(() => {
    refetchProjects()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getProjectById = async (projectId: string): Promise<Project | null> => {
    try {
      const response = await fetchProjectById(mapper.projects.getById.replace(':id', projectId))
      return normaliseProject(response)
    } catch {
      return null
    }
  }

  const normaliseThreadList = (response: any): ProjectThread[] =>
    Array.isArray(response)
      ? response
      : response?.threads ?? response?.items ?? response?.data ?? []

  const getProjectThreads = async (
    projectId: string,
    offset = 0,
    limit = 20
  ): Promise<ProjectThread[]> => {
    try {
      const url = `${mapper.projects.getThreads.replace(
        ':id',
        projectId
      )}?offset=${offset}&limit=${limit}`
      const response = await fetchProjectThreads(url)
      return normaliseThreadList(response)
    } catch {
      return []
    }
  }

  const moveThreadToProject = async (projectId: string, threadId: string): Promise<boolean> => {
    try {
      const response = await moveThreadPost({
        url: mapper.projects.moveThread.replace(':id', projectId).replace(':threadId', threadId),
        method: 'put'
      })
      // A successful move echoes back the updated project entity.
      return !!normaliseProject(response)
    } catch {
      toast.error('Error moving chat to project')
      return false
    }
  }

  const removeThreadFromProject = async (projectId: string, threadId: string): Promise<boolean> => {
    try {
      const response = await removeThreadItem({
        url: mapper.projects.removeThread.replace(':id', projectId).replace(':threadId', threadId)
      })
      // useDelete returns the raw fetch Response and does not throw on HTTP errors.
      const ok = response?.ok ?? response?.status === undefined
      if (!ok) {
        toast.error('Error removing chat from project')
        return false
      }
      return true
    } catch {
      toast.error('Error removing chat from project')
      return false
    }
  }

  // Uploads a raw file to the shared resource store (same endpoint ChatInput uses),
  // returning the created resource. Kept here (rather than reusing chatService) so the
  // request isn't tied to the chat stream's abort controller.
  const uploadProjectResource = async (file: File): Promise<any> => {
    const formData = new FormData()
    formData.append('file', file)

    return await uploadResourcePost({
      url: mapper.nexus.uploadResource,
      data: formData,
      hasFiles: true
    })
  }

  // Attaches an existing resource (already-uploaded file) to the project.
  const addFileToProject = async (projectId: string, resourceId: string): Promise<boolean> => {
    try {
      const response = await addFilePost({
        url: mapper.projects.addFile.replace(':id', projectId).replace(':resourceId', resourceId)
      })
      // usePost returns the parsed JSON on success, or the caught error object on failure
      // (it doesn't inspect res.ok). Treat any non-error, non-failure payload as success so
      // we don't drop a file the server actually saved when the response isn't a project shape.
      if (response instanceof Error) return false
      if (response?.error || response?.statusCode >= 400 || response?.status === 'error') {
        return false
      }
      return !!response
    } catch {
      toast.error('Error adding file to project')
      return false
    }
  }

  const normaliseFileList = (response: any): ProjectFile[] =>
    Array.isArray(response) ? response : response?.files ?? response?.items ?? response?.data ?? []

  const getProjectFiles = async (projectId: string): Promise<ProjectFile[]> => {
    try {
      const response = await fetchProjectFiles(mapper.projects.getFiles.replace(':id', projectId))
      return normaliseFileList(response)
    } catch {
      return []
    }
  }

  // Detaches a file from the project (soft-deletes the link; resource is not deleted).
  const removeFileFromProject = async (projectId: string, resourceId: string): Promise<boolean> => {
    try {
      const response = await removeFileItem({
        url: mapper.projects.deleteFile.replace(':id', projectId).replace(':resourceId', resourceId)
      })
      // useDelete returns the raw fetch Response and does not throw on HTTP errors.
      const ok = response?.ok ?? response?.status === undefined
      if (!ok) {
        toast.error('Error removing file from project')
        return false
      }
      return true
    } catch {
      toast.error('Error removing file from project')
      return false
    }
  }

  const normaliseDataBoxList = (response: any): ProjectDataBoxSelection[] => {
    const list = Array.isArray(response)
      ? response
      : response?.personalDataBoxes ?? response?.items ?? response?.data ?? []
    return (Array.isArray(list) ? list : []).map((box: any) => ({
      personalDataBoxId: box.personalDataBoxId ?? box.id,
      selectionMode: box.selectionMode ?? 'SelectedSubset',
      resourceIds: box.resourceIds ?? [],
      dataProductIds: box.dataProductIds ?? []
    }))
  }

  // Returns the project's currently attached personal databoxes (full collection).
  const getProjectDataBoxes = async (projectId: string): Promise<ProjectDataBoxSelection[]> => {
    try {
      const response = await fetchProjectDataBoxes(
        mapper.projects.personalDataBoxes.replace(':id', projectId)
      )
      return normaliseDataBoxList(response)
    } catch {
      return []
    }
  }

  // Replaces the project's full personal-databox collection (PUT semantics).
  const setProjectDataBoxes = async (
    projectId: string,
    personalDataBoxes: ProjectDataBoxSelection[]
  ): Promise<boolean> => {
    try {
      const response = await saveProjectDataBoxes({
        url: mapper.projects.personalDataBoxes.replace(':id', projectId),
        data: { personalDataBoxes },
        method: 'put'
      })
      if (response instanceof Error) return false
      if (response?.error || response?.statusCode >= 400 || response?.status === 'error') {
        return false
      }
      return true
    } catch {
      toast.error('Error updating project databoxes')
      return false
    }
  }

  // Attaches a subset of databox documents to the project. Reads the current
  // collection, merges the new resource ids into the matching databox (or adds a
  // new SelectedSubset entry), then PUTs the full collection back.
  const attachDataboxFilesToProject = async (
    projectId: string,
    selections: { databoxId: string; resourceIds: string[] }[]
  ): Promise<boolean> => {
    const current = await getProjectDataBoxes(projectId)
    const byId = new Map<string, ProjectDataBoxSelection>(
      current.map((box) => [box.personalDataBoxId, box])
    )

    for (const { databoxId, resourceIds } of selections) {
      if (!databoxId || !resourceIds.length) continue
      const existing = byId.get(databoxId)
      // A databox already attached as "All" already includes these documents.
      if (existing?.selectionMode === 'All') continue
      const merged = new Set([...(existing?.resourceIds ?? []), ...resourceIds])
      byId.set(databoxId, {
        personalDataBoxId: databoxId,
        selectionMode: 'SelectedSubset',
        resourceIds: Array.from(merged),
        dataProductIds: existing?.dataProductIds ?? []
      })
    }

    return setProjectDataBoxes(projectId, Array.from(byId.values()))
  }

  // Detaches a single databox document by removing its resource id from the
  // matching databox (dropping the databox entry entirely when it becomes empty).
  const removeDataboxFileFromProject = async (
    projectId: string,
    databoxId: string,
    resourceId: string
  ): Promise<boolean> => {
    const current = await getProjectDataBoxes(projectId)
    const next: ProjectDataBoxSelection[] = []
    for (const box of current) {
      if (box.personalDataBoxId !== databoxId) {
        next.push(box)
        continue
      }
      const resourceIds = (box.resourceIds ?? []).filter((id) => id !== resourceId)
      // Drop the databox if it no longer references any document or data product.
      if (resourceIds.length === 0 && (box.dataProductIds?.length ?? 0) === 0) continue
      next.push({ ...box, selectionMode: 'SelectedSubset', resourceIds })
    }
    return setProjectDataBoxes(projectId, next)
  }

  const createProject = async (payload: ProjectPayload): Promise<Project | null> => {
    try {
      const response = await createProjectPost({
        url: mapper.projects.create,
        data: payload
      })
      return normaliseProject(response)
    } catch {
      toast.error('Error creating project')
      return null
    }
  }

  const updateProject = async (
    projectId: string,
    payload: ProjectPayload
  ): Promise<Project | null> => {
    try {
      const response = await updateProjectPost({
        url: mapper.projects.update.replace(':id', projectId),
        data: payload,
        method: 'put'
      })
      return normaliseProject(response)
    } catch {
      toast.error('Error updating project')
      return null
    }
  }

  const deleteProject = async (projectId: string): Promise<boolean> => {
    try {
      const response = await deleteProjectItem({
        url: mapper.projects.delete.replace(':id', projectId)
      })
      // useDelete returns the raw fetch Response and does not throw on HTTP errors.
      const ok = response?.ok ?? response?.status === undefined
      if (!ok) {
        toast.error('Error deleting project')
        return false
      }
      return true
    } catch {
      toast.error('Error deleting project')
      return false
    }
  }

  return {
    projects,
    isLoadingProjects,
    refetchProjects,
    getProjectById,
    createProject,
    isCreatingProject,
    updateProject,
    isUpdatingProject,
    getProjectThreads,
    isLoadingProjectThreads,
    moveThreadToProject,
    isMovingThread,
    removeThreadFromProject,
    isRemovingThread,
    addFileToProject,
    isAddingFile,
    uploadProjectResource,
    isUploadingFile,
    getProjectFiles,
    isLoadingProjectFiles,
    removeFileFromProject,
    isRemovingFile,
    getProjectDataBoxes,
    isLoadingDataBoxes,
    attachDataboxFilesToProject,
    removeDataboxFileFromProject,
    isSavingDataBoxes,
    deleteProject,
    isDeletingProject
  }
}
